import { Menu as Ark } from '@ark-ui/react/menu';
import { Portal } from '@ark-ui/react/portal';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface MenuItem {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export type MenuEntry = MenuItem | 'separator' | { group: string };

export interface MenuProps {
  trigger: ReactNode;
  items: MenuEntry[];
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  className?: string;
  /** Открыть в точке (контекстное меню). */
  anchorPoint?: { x: number; y: number } | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function Menu({ trigger, items, side = 'bottom', align = 'start', className, anchorPoint, open, onOpenChange }: MenuProps) {
  const byValue = new Map<string, MenuItem>();
  for (const it of items) if (typeof it === 'object' && 'value' in it) byValue.set(it.value, it);
  return (
    <Ark.Root
      positioning={{ placement: align === 'center' ? side : `${side}-${align}`, gutter: 6 }}
      onSelect={(d) => byValue.get(d.value)?.onSelect()}
      anchorPoint={anchorPoint ?? undefined}
      open={open}
      onOpenChange={(d) => onOpenChange?.(d.open)}
      lazyMount
      unmountOnExit
    >
      {trigger !== null && <Ark.Trigger asChild>{trigger}</Ark.Trigger>}
      <Portal>
        <Ark.Positioner className="z-[1100]">
          <MenuContent items={items} className={className} />
        </Ark.Positioner>
      </Portal>
    </Ark.Root>
  );
}

function MenuContent({ items, className }: { items: MenuEntry[]; className?: string }) {
  return (
    <Ark.Content className={cn('glass min-w-52 p-1.5 outline-none data-[state=open]:animate-pop-in', className)}>
      {items.map((it, i) => {
        if (it === 'separator') return <Ark.Separator key={`s${i}`} className="mx-1 my-1.5 border-t border-border" />;
        if ('group' in it)
          return (
            <div key={`g${i}`} className="px-2.5 pt-2 pb-1 text-[11px] font-medium tracking-wide text-fg-subtle uppercase">
              {it.group}
            </div>
          );
        return (
          <Ark.Item
            key={it.value}
            value={it.value}
            disabled={it.disabled}
            className={cn(
              'flex min-h-8 cursor-pointer items-center gap-2.5 rounded-[8px] px-2.5 py-1 text-[13px] outline-none select-none',
              'data-[highlighted]:bg-hover data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40',
              it.danger ? 'text-danger' : 'text-fg',
              '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:stroke-[1.75] [&_svg]:text-fg-muted',
            )}
          >
            {it.icon}
            <span className="flex-1 truncate">{it.label}</span>
            {it.shortcut && <kbd className="font-sans text-[11px] text-fg-subtle">{it.shortcut}</kbd>}
          </Ark.Item>
        );
      })}
    </Ark.Content>
  );
}
